/**
 * Machine-readable reason on the proxy's structured error body
 * (`{code, message, reason}`) when a request routes to a branch whose
 * working database has not been opened/built yet. Surfaces as a 409;
 * the recovery is to open the branch.
 */
export const BRANCH_NOT_OPEN_REASON = 'BRANCH_NOT_OPEN'

/**
 * Extracts the machine-readable `reason` from the proxy's structured error
 * body (`{code, message, reason}`) on an RTK Query error; undefined when the
 * error carries no structured reason. Commit-flow reasons: NOTHING_TO_COMMIT,
 * HEAD_MOVED, BRANCH_NOT_OPEN, BRANCH_PROTECTED.
 */
export function getApiErrorReason(err: unknown): string | undefined {
  if (!err || typeof err !== 'object' || !('data' in err)) return undefined
  const data = (err as { data?: unknown }).data
  if (!data || typeof data !== 'object' || !('reason' in data)) return undefined
  const reason = (data as { reason?: unknown }).reason
  return typeof reason === 'string' ? reason : undefined
}

/**
 * Reports whether an RTK Query error is the proxy's structured
 * "branch not open" 409, recognized by reason — not string matching.
 */
export function isBranchNotOpenError(err: unknown): boolean {
  return getApiErrorReason(err) === BRANCH_NOT_OPEN_REASON
}

/**
 * Machine-readable reason on the proxy's structured error body when a graph
 * write routes to a protected branch (main, or any branch a user protected).
 * Surfaces as a 409; the recovery is to create or switch to a writable branch.
 */
export const BRANCH_PROTECTED_REASON = 'BRANCH_PROTECTED'

/**
 * Reports whether an RTK Query error is the proxy's structural
 * protected-branch write refusal — by reason code, never message text.
 */
export function isBranchProtectedError(err: unknown): boolean {
  return getApiErrorReason(err) === BRANCH_PROTECTED_REASON
}

/**
 * Machine-readable reason on the proxy's structured error body when a restore
 * targets a commit outside the branch's own history (its first-parent line
 * down to and including the fork point). Surfaces as a 409.
 */
export const COMMIT_NOT_ON_BRANCH_REASON = 'COMMIT_NOT_ON_BRANCH'

/**
 * Reports whether an RTK Query error is the proxy's structural
 * commit-not-on-branch restore refusal — by reason code, never message text.
 */
export function isCommitNotOnBranchError(err: unknown): boolean {
  return getApiErrorReason(err) === COMMIT_NOT_ON_BRANCH_REASON
}

export const NOTHING_TO_REVERT_REASON = 'NOTHING_TO_REVERT'
export const CANNOT_REVERT_ROOT_REASON = 'CANNOT_REVERT_ROOT'

/**
 * Machine-readable reason when a merge is attempted with a prepare stage whose
 * pinned heads have moved since prepare — the previewed plan is stale. The
 * recovery is to refresh the preview (re-prepare) and merge again.
 */
export const STALE_PREVIEW_REASON = 'STALE_PREVIEW'
